import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, Card, Row, Col, Statistic, Tabs, Table, Tag, 
  Button, Descriptions, Badge, Divider, Avatar,
  message, Spin, Alert, Space, Modal, Form, Input, Select,
  Empty, InputNumber, Image
} from 'antd';
import { 
  ArrowLeftOutlined, UserOutlined, WalletOutlined, 
  TeamOutlined,  SafetyCertificateOutlined,
  SettingOutlined, KeyOutlined, 
  CheckCircleOutlined, CloseCircleOutlined,
  TransactionOutlined,
  CreditCardOutlined,
  BellOutlined
} from '@ant-design/icons';
import { useAdmin } from '../../hooks/useAdmin';
import api from '../../services/api';
import moment from 'moment';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    getUserDetails, getUserStats, getUserWallet, getUserLogs, 
    getUserReferrals, updateUser, resetUserPassword, 
    verifyKYC, updateUserBalance, loading 
  } = useAdmin();

  // États
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isBalanceModalVisible, setIsBalanceModalVisible] = useState(false);
  const [isNotifyModalVisible, setIsNotifyModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [balanceForm] = Form.useForm();
  const [notifyForm] = Form.useForm();

  // Chargement des données
  const fetchAllData = useCallback(async () => {
    try {
      const [detailsRes, statsRes, walletRes, , referralsRes] = await Promise.all([
        getUserDetails(id),
        getUserStats(id),
        getUserWallet(id),
        getUserLogs(id),
        getUserReferrals(id)
      ]);

      if (detailsRes.success) setUserData(detailsRes.data);
      if (statsRes.success) setStats(statsRes.data);
      if (walletRes.success) setWalletInfo(walletRes.data);
      if (referralsRes.success) setReferrals(referralsRes.data);
    } catch (err) {
      message.error('Erreur lors du chargement des données utilisateur');
    }
  }, [id, getUserDetails, getUserStats, getUserWallet, getUserLogs, getUserReferrals]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Handlers
  const handleUpdateUser = async (values) => {
    const res = await updateUser(id, values);
    if (res.success) {
      message.success('Utilisateur mis à jour');
      setIsEditModalVisible(false);
      fetchAllData();
    }
  };

  const handleUpdateBalance = async (values) => {
    const res = await updateUserBalance(id, values);
    if (res.success) {
      message.success('Balance mise à jour avec succès');
      setIsBalanceModalVisible(false);
      balanceForm.resetFields();
      fetchAllData();
    }
  };

  const handleKYCAction = async (status, notes = '') => {
    const res = await verifyKYC(id, { status, notes });
    if (res.success) {
      message.success(`KYC ${status === 'verified' ? 'approuvé' : 'rejeté'}`);
      fetchAllData();
    }
  };

  const handleResetPassword = async () => {
    Modal.confirm({
      title: 'Réinitialiser le mot de passe ?',
      content: 'Un nouveau mot de passe sera généré et envoyé à l\'utilisateur.',
      onOk: async () => {
        const res = await resetUserPassword(id);
        if (res.success) message.success('Mot de passe réinitialisé');
      }
    });
  };

  if (loading && !userData) {
    return <div className="flex justify-center items-center h-screen"><Spin size="large" tip="Chargement du Command Center..." /></div>;
  }

  if (!userData) {
    return <Alert message="Erreur" description="Utilisateur non trouvé" type="error" showIcon />;
  }

  return (
    <div className="admin-user-detail-page">
      {/* Header Section */}
      <Card className="mb-6 shadow-sm">
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/users')}>Retour</Button>
              <Avatar size={64} icon={<UserOutlined />} src={userData.avatar} />
              <div>
                <Title level={3} style={{ margin: 0 }}>{userData.username}</Title>
                <Text type="secondary">{userData.email}</Text>
              </div>
              <Space direction="vertical" size={0}>
                <Tag color={userData.account_status === 'active' ? 'green' : 'red'}>
                  {userData.account_status?.toUpperCase()}
                </Tag>
                <Tag color="blue" style={{ marginTop: 4 }}>{userData.role?.toUpperCase()}</Tag>
              </Space>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button 
                type="primary" 
                icon={<TransactionOutlined />} 
                onClick={() => setIsBalanceModalVisible(true)}
              >
                Ajuster Solde
              </Button>
              <Button 
                icon={<BellOutlined />} 
                onClick={() => setIsNotifyModalVisible(true)}
              >
                Notifier
              </Button>
              <Button icon={<SettingOutlined />} onClick={() => {
                form.setFieldsValue(userData);
                setIsEditModalVisible(true);
              }}>Modifier Profil</Button>
              <Button icon={<KeyOutlined />} onClick={handleResetPassword}>Reset Pass</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Quick Stats Row */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="shadow-sm border-l-4 border-blue-500">
            <Statistic 
              title="Solde Total Estimé" 
              value={walletInfo?.total_balance || 0} 
              precision={2} 
              suffix="USDT"
              prefix={<WalletOutlined />} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="shadow-sm border-l-4 border-green-500">
            <Statistic 
              title="Total Investi" 
              value={stats?.total_invested || 0} 
              precision={2} 
              prefix={<CreditCardOutlined />} 
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="shadow-sm border-l-4 border-purple-500">
            <Statistic 
              title="Commissions Totales" 
              value={stats?.total_commissions || 0} 
              precision={2} 
              prefix={<TeamOutlined />} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="shadow-sm border-l-4 border-orange-500">
            <Statistic 
              title="KYC Statut" 
              value={userData.kyc_status?.toUpperCase()} 
              valueStyle={{ color: userData.kyc_status === 'verified' ? '#3f8600' : '#cf1322' }}
              prefix={<SafetyCertificateOutlined />} 
            />
          </Card>
        </Col>
      </Row>

      {/* Detailed Content Tabs */}
      <Card className="shadow-sm">
        <Tabs defaultActiveKey="1" type="card">
          <TabPane tab="Profil & KYC" key="1">
            <Row gutter={24}>
              <Col lg={14}>
                <Descriptions title="Informations Personnelles" bordered column={1}>
                  <Descriptions.Item label="Nom Complet">{userData.full_name || 'Non renseigné'}</Descriptions.Item>
                  <Descriptions.Item label="Téléphone">{userData.phone_number || 'Non renseigné'}</Descriptions.Item>
                  <Descriptions.Item label="Telegram">
                    {userData.telegram_username ? (
                      <a href={`https://t.me/${userData.telegram_username.replace('@', '')}`} target="_blank" rel="noreferrer">
                        {userData.telegram_username.startsWith('@') ? userData.telegram_username : `@${userData.telegram_username}`}
                      </a>
                    ) : 'Non lié'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date de naissance">{userData.date_of_birth ? moment(userData.date_of_birth).format('DD/MM/YYYY') : 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Pays">{userData.country || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Adresse">{userData.address || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Inscription">{moment(userData.created_at).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
                  <Descriptions.Item label="Dernière IP">{userData.last_ip || 'N/A'}</Descriptions.Item>
                </Descriptions>
              </Col>
              <Col lg={10}>
                <Title level={5}>Vérification KYC</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text type="secondary">ID Document</Text>
                    <div className="kyc-img-container mt-2">
                      {userData.id_document ? (
                        <Image src={`${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}/${userData.id_document.replace(/\\/g, '/')}`} />
                      ) : <Empty description="Pas d'image" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">Selfie</Text>
                    <div className="kyc-img-container mt-2">
                      {userData.selfie_verification ? (
                        <Image src={`${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}/${userData.selfie_verification.replace(/\\/g, '/')}`} />
                      ) : <Empty description="Pas d'image" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    </div>
                  </Col>
                </Row>
                <Divider />
                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Button 
                    type="primary" 
                    icon={<CheckCircleOutlined />} 
                    onClick={() => handleKYCAction('verified')}
                    disabled={userData.kyc_status === 'verified'}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Approuver
                  </Button>
                  <Button 
                    danger 
                    icon={<CloseCircleOutlined />} 
                    onClick={() => handleKYCAction('rejected')}
                  >
                    Rejeter
                  </Button>
                </Space>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Portefeuilles (Wallets)" key="2">
            <Table 
              dataSource={walletInfo?.wallets || []} 
              pagination={false}
              rowKey={(r) => `${r.asset}_${r.wallet_type}`}
              columns={[
                { title: 'Actif', dataIndex: 'asset', key: 'asset', render: a => <Tag color="gold">{a}</Tag> },
                { title: 'Type', dataIndex: 'wallet_type', key: 'type', render: t => <Tag color="blue">{t.toUpperCase()}</Tag> },
                { title: 'Disponible', dataIndex: 'balance', key: 'bal', render: v => <Text strong>{parseFloat(v).toFixed(8)}</Text> },
                { title: 'Verrouillé', dataIndex: 'locked', key: 'lock', render: v => parseFloat(v).toFixed(8) },
                { title: 'Valeur USDT', key: 'val', render: (_, r) => `$${(parseFloat(r.balance) + parseFloat(r.locked)).toFixed(2)}` }
              ]}
            />
          </TabPane>

          <TabPane tab="Transactions & Logs" key="3">
            <Table 
              dataSource={walletInfo?.recent_transactions || []} 
              pagination={{ pageSize: 10 }}
              rowKey="id"
              columns={[
                { title: 'Date', dataIndex: 'created_at', render: d => moment(d).format('DD/MM HH:mm') },
                { title: 'Type', dataIndex: 'transaction_type', render: t => <Tag>{t.toUpperCase()}</Tag> },
                { title: 'Montant', dataIndex: 'change_amount', render: v => <Text type={v > 0 ? 'success' : 'danger'}>{v > 0 ? `+${v}` : v}</Text> },
                { title: 'Description', dataIndex: 'description' }
              ]}
            />
          </TabPane>
          
          <TabPane tab="Équipe MLM" key="4">
             <Row gutter={16}>
               <Col span={8}>
                 <Card size="small" title="Directs">
                   <Statistic value={referrals?.total_referrals || 0} />
                 </Card>
               </Col>
               <Col span={8}>
                 <Card size="small" title="Parrainé par">
                   <Text strong>{referrals?.referrer?.username || 'Racine'}</Text>
                 </Card>
               </Col>
               <Col span={8}>
                 <Card size="small" title="Volume Équipe">
                   <Statistic value={stats?.team_volume || 0} suffix="USDT" />
                 </Card>
               </Col>
             </Row>
             <Divider />
             <Table 
                dataSource={referrals?.referrals || []} 
                rowKey="id"
                columns={[
                  { title: 'Username', dataIndex: 'username' },
                  { title: 'Email', dataIndex: 'email' },
                  { title: 'Status', dataIndex: 'account_status', render: s => <Badge status={s === 'active' ? 'success' : 'error'} text={s} /> }
                ]}
             />
          </TabPane>
        </Tabs>
      </Card>

      {/* Modals Section */}
      
      {/* Balance Adjustment Modal */}
      <Modal
        title={`Ajuster Solde: ${userData.username}`}
        open={isBalanceModalVisible}
        onCancel={() => setIsBalanceModalVisible(false)}
        onOk={() => balanceForm.submit()}
      >
        <Form form={balanceForm} layout="vertical" onFinish={handleUpdateBalance}>
          <Form.Item name="asset" label="Actif" initialValue="USDT" rules={[{ required: true }]}>
            <Select>
              <Option value="USDT">USDT</Option>
              <Option value="BTC">BTC</Option>
              <Option value="ETH">ETH</Option>
            </Select>
          </Form.Item>
          <Form.Item name="wallet_type" label="Type de Wallet" initialValue="spot" rules={[{ required: true }]}>
            <Select>
              <Option value="spot">Spot</Option>
              <Option value="futures">Futures</Option>
              <Option value="investment">Investment</Option>
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Montant à ajouter/retirer" help="Utilisez un nombre négatif pour débiter" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} precision={8} />
          </Form.Item>
          <Form.Item name="description" label="Raison / Note Admin" rules={[{ required: true }]}>
            <Input.TextArea placeholder="Ex: Correction de solde, Bonus manuel..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Profil Edit Modal */}
      <Modal
        title="Édition Profil Administrateur"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdateUser}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="username" label="Username"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="email" label="Email"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="phone_number" label="Téléphone"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="telegram_username" label="Telegram"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="role" label="Rôle Système">
            <Select>
              <Option value="user">Utilisateur Standard</Option>
              <Option value="leader">Leader MLM</Option>
              <Option value="manager">Manager Opérationnel</Option>
              <Option value="admin">Administrateur Système</Option>
            </Select>
          </Form.Item>
          <Form.Item name="account_status" label="État du compte">
            <Select>
              <Option value="active">Actif</Option>
              <Option value="suspended">Suspendu (Temporaire)</Option>
              <Option value="banned">Banni (Définitif)</Option>
            </Select>
          </Form.Item>
          <Divider />
          <Form.Item name="kyc_status" label="Statut KYC Manuel">
            <Select>
              <Option value="pending">En attente</Option>
              <Option value="verified">Vérifié</Option>
              <Option value="rejected">Rejeté</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Notification Modal */}
      <Modal
        title={`Envoyer une notification à ${userData.username}`}
        open={isNotifyModalVisible}
        onCancel={() => setIsNotifyModalVisible(false)}
        onOk={() => notifyForm.submit()}
        confirmLoading={loading}
      >
        <Form form={notifyForm} layout="vertical" onFinish={async (values) => {
            const res = await api.post('/admin/notifications/send', {
                userId: id,
                ...values
            });
            if (res.data.success) {
                message.success('Notification envoyée');
                setIsNotifyModalVisible(false);
                notifyForm.resetFields();
            }
        }}>
          <Form.Item name="type" label="Type" initialValue="info" rules={[{ required: true }]}>
            <Select>
              <Option value="info">Information</Option>
              <Option value="success">Succès</Option>
              <Option value="warning">Avertissement</Option>
              <Option value="error">Erreur</Option>
            </Select>
          </Form.Item>
          <Form.Item name="title" label="Titre" rules={[{ required: true }]}>
            <Input placeholder="Titre de la notification" />
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true }]}>
            <Input.TextArea rows={4} placeholder="Contenu du message..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserDetail;
